import { eachDayOfInterval, format, isSameDay, parseISO, startOfDay, startOfMonth } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DayClickEventHandler, DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

const DATE_FORMAT = 'yyyy-MM-dd';

// Color bucket each booked day paints in. Callers pre-bucket their domain
// status (assignment workflow, shift workflow, etc.) into one of these so
// BaseCalendar stays workflow-agnostic. `'blocked'` paints a soft "not
// available" treatment for days the caller wants to visually exclude but
// has no assignment id for (e.g. cross-org bookings inferred client-side).
export type BookedRangeBucket = 'pending' | 'confirmed' | 'completed' | 'blocked';

export type BookedRange = {
  from: Date;
  to: Date;
  bucket: BookedRangeBucket;
  /**
   * Assignment id used to route clicks to the drawer. Omit for `'blocked'`
   * ranges and any other case where the day should paint but not be
   * clickable.
   */
  assignmentId?: string;
};

export interface BaseCalendarProps {
  /**
   * Array of available date strings in ISO format (e.g., "2026-01-01")
   */
  availableDates: string[];
  /**
   * Subset of `availableDates` that the provider explicitly marked open.
   * Days in `availableDates` but not in this list are "auto-included" via
   * the provider's `schedule_type` (OPEN/STRICT) and paint with a softer
   * visual treatment so the caller can see they weren't directly chosen.
   * Omit to disable the distinction — all available days render the same.
   */
  explicitAvailableDates?: string[];
  /**
   * Currently selected dates
   */
  selectedDates: Date[];
  /**
   * Callback when selected dates change
   */
  onSelectedDatesChange: (dates: Date[]) => void;
  /**
   * Optional array of disabled dates (dates that cannot be selected)
   */
  disabledDates?: Date[];
  /**
   * Optional current month to display (defaults to first selected date or today)
   */
  currentMonth?: Date;
  /**
   * Optional callback when month changes
   */
  onMonthChange?: (date: Date) => void;
  /**
   * Optional number of months to display side by side
   */
  numberOfMonths?: number;
  /**
   * Optional CSS class name
   */
  className?: string;
  /**
   * Optional flag to disable the calendar
   */
  disabled?: boolean;
  /**
   * Optional date from which to start showing dates (e.g. today or start of month)
   */
  fromDate?: Date;
  /**
   * Optional upper bound for navigation — react-day-picker will not allow
   * navigating past the month containing this date.
   */
  toDate?: Date;
  /**
   * Optional booked assignment date ranges to render as colored markers on the calendar.
   * Each range expands to a per-day marker grouped by status (pending/confirmed/completed).
   * Unknown statuses are ignored.
   */
  bookedRanges?: BookedRange[];
  /**
   * Optional callback fired when a booked date is clicked. When provided and the
   * clicked day is part of a booked range, this is called instead of the normal
   * selection toggle.
   */
  onBookedDateClick?: (assignmentId: string) => void;
  /**
   * When true, the calendar table stretches to fill its container width and
   * day cells distribute evenly across each row (1/7 each). Defaults to false,
   * which preserves the existing fixed-cell-width rendering.
   *
   * Intended for single-month layouts where the calendar should match the
   * width of surrounding header/content. Avoid on multi-month layouts unless
   * the container is wide enough to keep cells legible.
   */
  fullWidth?: boolean;
  /**
   * When true, any non-disabled date is clickable and will fire
   * onSelectedDatesChange. Default false: clicks are gated to dates already
   * in `availableDates` (the original behavior — used by scheduling flows
   * where users pick from a predefined pool of pre-marked dates).
   *
   * Pass `true` for flows where the click itself is what creates availability
   * (e.g. the Provider home schedule calendar).
   */
  clickableAllDates?: boolean;
  /**
   * ISO date strings that already carry a shift in the caller's context
   * (e.g. the assignment being edited). They paint amber so the user can
   * tell them apart from open days at a glance, and they STAY selectable —
   * a plain click toggles them like an available day even though they are
   * not in `availableDates` (the provider's availability already subtracts
   * booked days). Used by the drawer's add-shift flow for the rare split
   * day where a clinician works two separate shifts on one date.
   *
   * Deliberately excluded from shift-click range adds: a second shift on an
   * already-scheduled day must be an explicit single pick, never swept up
   * by a range gesture.
   */
  scheduledDates?: string[];
  /**
   * When true, dates strictly before today (local midnight) are read-only:
   *  - they receive a faded `.rdp-day_past` modifier class
   *  - plain clicks on them do not fire onSelectedDatesChange
   *  - shift-click range actions skip them entirely
   * Booked past dates still route to onBookedDateClick — the drawer is the
   * read-only view for those. Default false preserves the original
   * fully-editable behavior used by other calendars.
   */
  pastReadOnly?: boolean;
}

export function BaseCalendar({
  availableDates,
  explicitAvailableDates,
  selectedDates,
  onSelectedDatesChange,
  disabledDates = [],
  currentMonth,
  onMonthChange,
  numberOfMonths = 1,
  className,
  disabled = false,
  fromDate,
  bookedRanges,
  onBookedDateClick,
  fullWidth = false,
  clickableAllDates = false,
  scheduledDates,
  pastReadOnly = false,
  toDate,
}: BaseCalendarProps) {
  // Anchor for shift-click range selection. Set on every plain click that
  // toggles a date; persists until the next plain click. Shift-click derives
  // the action (add or remove) from whether the anchor is currently
  // selected, so the entire range follows the intent the user just expressed
  // with their last plain click.
  const [anchorDate, setAnchorDate] = useState<Date | null>(null);

  // Default fromDate to the start of the current month if not provided
  const effectiveFromDate = useMemo(() => fromDate ?? startOfMonth(new Date()), [fromDate]);

  // Local-midnight today. Stable for the life of the component — if the
  // user keeps the page open across midnight the boundary won't shift,
  // which is fine for this UX (the worst case is the just-flipped day
  // stays editable until the next mount).
  const todayStart = useMemo(() => startOfDay(new Date()), []);
  const isPastDate = useCallback(
    (day: Date) => pastReadOnly && day.getTime() < todayStart.getTime(),
    [pastReadOnly, todayStart]
  );

  // Internal month state - only used if onMonthChange is not provided (uncontrolled mode)
  const [internalMonth, setInternalMonth] = useState<Date>(() => {
    if (currentMonth) return currentMonth;
    if (selectedDates.length > 0) return selectedDates[0];
    return startOfMonth(new Date());
  });

  // Update internal month when currentMonth prop changes (controlled mode)
  useEffect(() => {
    if (currentMonth) {
      setInternalMonth(currentMonth);
    }
  }, [currentMonth]);

  const availableDatesSet = useMemo(() => {
    return new Set(availableDates.map((dateStr) => format(parseISO(dateStr), DATE_FORMAT)));
  }, [availableDates]);

  // Set of ISO keys the provider explicitly marked open. When the prop is
  // omitted we treat every available day as explicit (no auto distinction).
  const explicitDatesSet = useMemo(() => {
    if (!explicitAvailableDates) return null;
    return new Set(explicitAvailableDates.map((d) => format(parseISO(d), DATE_FORMAT)));
  }, [explicitAvailableDates]);

  // Cache Date instances by ISO so the same date keeps the same reference
  // across renders. react-day-picker reconciles per-day cells partly by
  // identity of the Dates in `modifiers.available`; a fresh Date instance
  // every render makes RDP re-mount each cell and visibly repaint it
  // (a flicker). The cache is bounded by the calendar's date window.
  const dateCacheRef = useRef<Map<string, Date>>(new Map());
  const availableDatesAsDates = useMemo(() => {
    return availableDates.map((dateStr) => {
      const cached = dateCacheRef.current.get(dateStr);
      if (cached) return cached;
      const next = parseISO(dateStr);
      dateCacheRef.current.set(dateStr, next);
      return next;
    });
  }, [availableDates]);

  // Subset of available dates that aren't in the explicit set — receives the
  // softer "auto" visual treatment. Empty array when no explicit list was
  // provided, so the modifier matches nothing and behavior is unchanged.
  const availableAutoDates = useMemo(() => {
    if (!explicitDatesSet) return [];
    return availableDatesAsDates.filter((date) => !explicitDatesSet.has(format(date, DATE_FORMAT)));
  }, [availableDatesAsDates, explicitDatesSet]);

  // Already-scheduled days: painted amber and clickable on their own. Date
  // instances are cached the same way as availableDatesAsDates so RDP keeps
  // cell identity stable across renders.
  const scheduledDatesAsDates = useMemo(() => {
    return (scheduledDates ?? []).map((dateStr) => {
      const cached = dateCacheRef.current.get(dateStr);
      if (cached) return cached;
      const next = parseISO(dateStr);
      dateCacheRef.current.set(dateStr, next);
      return next;
    });
  }, [scheduledDates]);
  const scheduledDatesSet = useMemo(() => {
    return new Set(scheduledDatesAsDates.map((d) => format(d, DATE_FORMAT)));
  }, [scheduledDatesAsDates]);

  // Expand each booked range into per-day buckets grouped by status, plus a
  // date-key -> assignmentId lookup so clicks on booked days can be routed back.
  // Ranges without an `assignmentId` (e.g. `'blocked'` overlay days) paint but
  // are not added to the lookup, so clicks on them no-op.
  const {
    bookedPendingDates,
    bookedConfirmedDates,
    bookedCompletedDates,
    bookedBlockedDates,
    dateToAssignmentId,
  } = useMemo(() => {
    const pending: Date[] = [];
    const confirmed: Date[] = [];
    const completed: Date[] = [];
    const blocked: Date[] = [];
    const lookup = new Map<string, string>();

    (bookedRanges ?? []).forEach((range) => {
      const start = range.from < range.to ? range.from : range.to;
      const end = range.from < range.to ? range.to : range.from;
      const days = eachDayOfInterval({ start, end });

      let target: Date[];
      if (range.bucket === 'pending') {
        target = pending;
      } else if (range.bucket === 'confirmed') {
        target = confirmed;
      } else if (range.bucket === 'completed') {
        target = completed;
      } else {
        target = blocked;
      }

      days.forEach((day) => {
        target.push(day);
        if (range.assignmentId) {
          lookup.set(format(day, DATE_FORMAT), range.assignmentId);
        }
      });
    });

    return {
      bookedPendingDates: pending,
      bookedConfirmedDates: confirmed,
      bookedCompletedDates: completed,
      bookedBlockedDates: blocked,
      dateToAssignmentId: lookup,
    };
  }, [bookedRanges]);

  // Set of every booked date key (including blocked) — used by the click
  // handler to short-circuit selection toggling on any painted day.
  const allBookedDateKeys = useMemo(() => {
    const keys = new Set<string>();
    [bookedPendingDates, bookedConfirmedDates, bookedCompletedDates, bookedBlockedDates].forEach(
      (arr) => arr.forEach((d) => keys.add(format(d, DATE_FORMAT)))
    );
    return keys;
  }, [bookedPendingDates, bookedConfirmedDates, bookedCompletedDates, bookedBlockedDates]);

  const displayMonth = currentMonth ?? internalMonth;

  const handleMonthChange = (date: Date) => {
    if (onMonthChange) {
      // Controlled mode - parent handles state
      onMonthChange(date);
    } else {
      // Uncontrolled mode - update internal state
      setInternalMonth(date);
    }
  };

  const filterUniqueDates = (dates: Date[]): Date[] => {
    return Object.values(
      dates.reduce((acc: { [key: string]: Date }, date: Date) => {
        const key = format(date, DATE_FORMAT);
        acc[key] = date;
        return acc;
      }, {})
    ).sort((a, b) => a.getTime() - b.getTime());
  };

  /* eslint-disable sonarjs/cognitive-complexity */
  const handleDayClick: DayClickEventHandler = (date, modifiers, event) => {
    if (disabled || modifiers.disabled) {
      return;
    }

    const dateKey = format(date, DATE_FORMAT);
    const isBookedDate = allBookedDateKeys.has(dateKey);
    const shiftHeld = Boolean(event?.shiftKey);

    // Shift-click range: apply the anchor's intent (add or remove) to every
    // non-booked, clickable date between anchor and target. Action is
    // derived from the anchor's current selected state — that's whatever
    // the user's last plain click resulted in, which keeps the gesture
    // predictable ("extend the thing I just did across this range").
    if (shiftHeld && anchorDate) {
      const anchorSelected = selectedDates.some((d) => isSameDay(d, anchorDate));
      const action: 'add' | 'remove' = anchorSelected ? 'add' : 'remove';
      const [rangeStart, rangeEnd] = anchorDate < date ? [anchorDate, date] : [date, anchorDate];
      const rangeDays = eachDayOfInterval({ start: rangeStart, end: rangeEnd });

      let next: Date[];
      if (action === 'add') {
        const toAdd = rangeDays.filter((d) => {
          const k = format(d, DATE_FORMAT);
          if (allBookedDateKeys.has(k)) return false; // skip booked + blocked
          if (scheduledDatesSet.has(k)) return false; // explicit single picks only
          if (isPastDate(d)) return false; // skip past when pastReadOnly is on
          if (!clickableAllDates && !availableDatesSet.has(k)) return false;
          return !selectedDates.some((s) => isSameDay(s, d));
        });
        next = filterUniqueDates([...selectedDates, ...toAdd]);
      } else {
        const removeKeys = new Set(
          rangeDays
            .filter((d) => !allBookedDateKeys.has(format(d, DATE_FORMAT)))
            .filter((d) => !isPastDate(d)) // skip past when pastReadOnly is on
            .map((d) => format(d, DATE_FORMAT))
        );
        next = selectedDates.filter((d) => !removeKeys.has(format(d, DATE_FORMAT)));
      }

      onSelectedDatesChange(next);
      // Move the anchor to the target so users can chain shift-clicks. If
      // the target is booked we leave the anchor where it was — booked
      // dates aren't valid anchors (a plain click on one routes to the
      // drawer instead of toggling). Same for past targets: they aren't
      // valid anchors because plain clicks on them no-op.
      if (!isBookedDate && !isPastDate(date)) setAnchorDate(date);
      return;
    }

    // Plain click on a booked date: route to the drawer when we have an id
    // (status-bucketed shifts/assignments), otherwise no-op (blocked overlay).
    // Booked dates open the drawer in the past too — the drawer is the
    // read-only view for historical bookings.
    if (isBookedDate) {
      const assignmentId = dateToAssignmentId.get(dateKey);
      if (onBookedDateClick && assignmentId) {
        onBookedDateClick(assignmentId);
      }
      return;
    }

    // Past dates are read-only when pastReadOnly is set: no toggle, no
    // anchor change. Booked past dates were already handled above.
    if (isPastDate(date)) {
      return;
    }

    // Already-scheduled days are clickable even though the provider's
    // availability (and therefore `availableDates`) excludes booked days.
    if (!clickableAllDates && !availableDatesSet.has(dateKey) && !scheduledDatesSet.has(dateKey)) {
      return;
    }

    // Plain click: toggle the single date and set it as the new anchor.
    const datesToSelect = modifiers.selected
      ? selectedDates.filter((selectedDate) => !isSameDay(selectedDate, date))
      : filterUniqueDates([...selectedDates, date]);
    setAnchorDate(date);
    onSelectedDatesChange(datesToSelect);
  };

  const calendarClassName =
    [className, disabled ? 'rdp-disabled' : null, fullWidth ? 'rdp-full-width' : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <DayPicker
      mode="multiple"
      selected={selectedDates}
      disabled={disabledDates}
      month={displayMonth}
      fromDate={effectiveFromDate}
      toDate={toDate}
      numberOfMonths={numberOfMonths}
      className={calendarClassName}
      modifiers={{
        available: availableDatesAsDates,
        availableAuto: availableAutoDates,
        bookedPending: bookedPendingDates,
        bookedConfirmed: bookedConfirmedDates,
        bookedCompleted: bookedCompletedDates,
        bookedBlocked: bookedBlockedDates,
        scheduled: scheduledDatesAsDates,
        past: isPastDate,
      }}
      modifiersClassNames={{
        available: 'rdp-day_available',
        availableAuto: 'rdp-day_available_auto',
        bookedPending: 'rdp-day_booked_pending',
        bookedConfirmed: 'rdp-day_booked_confirmed',
        bookedCompleted: 'rdp-day_booked_completed',
        bookedBlocked: 'rdp-day_booked_blocked',
        scheduled: 'rdp-day_scheduled',
        past: 'rdp-day_past',
      }}
      onDayClick={handleDayClick}
      onMonthChange={handleMonthChange}
      weekStartsOn={0}
    />
  );
}
